/**
 * Utilitaires pour le formatage sécurisé des nombres
 */

/**
 * Formate un nombre de manière sécurisée avec toLocaleString()
 * @param {number|null|undefined} value - La valeur à formater
 * @param {string} locale - La locale (par défaut 'fr-FR')
 * @param {object} options - Options de formatage
 * @returns {string} - Le nombre formaté ou '0' si la valeur est invalide
 */
export const formatNumberSafe = (value, locale = 'fr-FR', options = {}) => {
  // Vérifier si la valeur est un nombre valide
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  
  // Convertir en nombre si c'est une chaîne
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Vérifier encore une fois si c'est un nombre valide après conversion
  if (isNaN(numValue)) {
    return '0';
  }
  
  return numValue.toLocaleString(locale, options);
};

/**
 * Formate une devise de manière sécurisée
 * @param {number|null|undefined} amount - Le montant à formater
 * @param {string} currency - La devise (par défaut 'FCFA')
 * @param {string} locale - La locale (par défaut 'fr-FR')
 * @returns {string} - Le montant formaté avec la devise
 */
export const formatCurrencySafe = (amount, currency = 'FCFA', locale = 'fr-FR') => {
  const formattedAmount = formatNumberSafe(amount, locale);
  return `${formattedAmount} ${currency}`;
};

/**
 * Formate un pourcentage de manière sécurisée
 * @param {number|null|undefined} value - La valeur à formater (déjà en pourcentage, ex: 85 pour 85%)
 * @param {number} decimals - Nombre de décimales (par défaut 0)
 * @returns {string} - Le pourcentage formaté
 */
export const formatPercentageSafe = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '0%';
  }
  
  return `${numValue.toFixed(decimals)}%`;
};

/**
 * Calcule un montant restant de manière sécurisée
 * @param {number|null|undefined} total - Le montant total
 * @param {number|null|undefined} received - Le montant reçu
 * @returns {number} - Le montant restant (toujours >= 0)
 */
export const calculateRemainingAmount = (total, received) => {
  const safeTotal = total || 0;
  const safeReceived = received || 0;
  const remaining = safeTotal - safeReceived;
  return Math.max(0, remaining); // S'assurer que le résultat n'est jamais négatif
};

/**
 * Calcule un pourcentage de progression de manière sécurisée
 * @param {number|null|undefined} received - Le montant reçu
 * @param {number|null|undefined} total - Le montant total
 * @returns {number} - Le pourcentage de progression (0-100)
 */
export const calculateProgressSafe = (received, total) => {
  const safeTotal = total || 0;
  const safeReceived = received || 0;
  
  if (safeTotal <= 0) {
    return 0;
  }
  
  const percentage = (safeReceived / safeTotal) * 100;
  return Math.min(100, Math.max(0, percentage)); // Entre 0 et 100
};

// Export par défaut
const numberFormatting = {
  formatNumberSafe,
  formatCurrencySafe,
  formatPercentageSafe,
  calculateRemainingAmount,
  calculateProgressSafe
};

export default numberFormatting;